import { useCallback, useEffect, useMemo, useState } from 'react';

import { ConfirmDialog } from 'primereact/confirmdialog';
import { useTranslation } from 'react-i18next';
import {
  Outlet,
  useBlocker,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import {
  type WorkflowTab,
  WorkflowTabBar,
} from '@/components/common/workflow-tab-bar';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { SemenPullPage } from '@/pages/semen-pull';

import { HomeTab } from './components/HomeTab';
import { SemenActions } from './components/HomeTab/SemenActions';
import { ShippingIntegrationTab } from './components/ShippingIntegrationTab';
import { useSemenAppContext } from './context/SemenAppContext';
import { SemenAppWrapper } from './context/SemenAppWrapper';

type SemenTab = 'home' | 'shipping-integration' | 'semen-pull';

const SEMEN_TABS: WorkflowTab<SemenTab>[] = [
  { key: 'home', label: 'Home', icon: 'pi pi-home' },
  {
    key: 'shipping-integration',
    label: 'Shipping Integration',
    icon: 'pi pi-truck',
  },
  { key: 'semen-pull', label: 'Semen Pull', icon: 'pi pi-arrow-down' },
];

const isSemenTab = (value: string | null): value is SemenTab =>
  value === 'home' ||
  value === 'shipping-integration' ||
  value === 'semen-pull';

const SemenTabsView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const {
    homeTabGridRef,
    refreshHomeTabGrid,
    selectedSemenRef,
    hasUnsavedChanges,
    clearUnsavedChanges,
  } = useSemenAppContext();
  const isSelectedSemenShippingEnabled = useFeatureFlag(
    'Selected_Semen_Shipping_Integration',
  );
  const isPersistSemenAcrossTabsEnabled = useFeatureFlag(
    'Open_New_Shipment_in_New_Window',
  );

  const blocker = useBlocker(
    ({ nextLocation }) =>
      isPersistSemenAcrossTabsEnabled &&
      hasUnsavedChanges &&
      !nextLocation.pathname.startsWith('/semen'),
  );

  const pathSegments = location.pathname.split('/').filter(Boolean);
  const currentRoute = pathSegments[pathSegments.length - 1];
  const routeTab: SemenTab =
    currentRoute === 'shipping-integration' || currentRoute === 'semen-pull'
      ? currentRoute
      : 'home';

  const queryTab = searchParams.get('tab');
  const activeTab: SemenTab = isPersistSemenAcrossTabsEnabled
    ? isSemenTab(queryTab)
      ? queryTab
      : routeTab
    : routeTab;

  // Keep-alive: remember which tabs have been visited so each panel is
  // lazy-mounted on first activation and then kept mounted (hidden when
  // inactive), avoiding remounts and re-fetches on tab return.
  const [visitedTabs, setVisitedTabs] = useState<Set<SemenTab>>(
    () => new Set<SemenTab>([activeTab]),
  );

  useEffect(() => {
    if (!isPersistSemenAcrossTabsEnabled) return;
    setVisitedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [isPersistSemenAcrossTabsEnabled, activeTab]);

  // Deep-link normalization: when the flag is on and the user lands on a tab
  // path route, redirect once to /semen?tab=... so the keep-alive container
  // stays mounted (pathname fixed at /semen).
  useEffect(() => {
    if (!isPersistSemenAcrossTabsEnabled) return;
    if (
      currentRoute === 'shipping-integration' ||
      currentRoute === 'semen-pull'
    ) {
      const params = new URLSearchParams(location.search);
      params.set('tab', currentRoute);
      navigate(`/semen?${params.toString()}`, { replace: true });
    }
  }, [
    isPersistSemenAcrossTabsEnabled,
    currentRoute,
    location.search,
    navigate,
  ]);

  const showSemenActions = isPersistSemenAcrossTabsEnabled
    ? activeTab === 'home'
    : location.pathname.endsWith('/semen');

  const handleTabChange = useCallback(
    (tab: SemenTab) => {
      if (isPersistSemenAcrossTabsEnabled) {
        const params = new URLSearchParams(location.search);
        params.set('tab', tab);
        setSearchParams(params, { replace: true });
        return;
      }

      const urlParams = new URLSearchParams(location.search);
      const search = urlParams.toString();
      const path = tab === 'home' ? '/semen' : `/semen/${tab}`;

      if (isSelectedSemenShippingEnabled && tab === 'shipping-integration') {
        const semenToShip = selectedSemenRef.current;
        navigate(`${path}${search ? `?${search}` : ''}`, {
          state: { shippingSemen: semenToShip },
        });
      } else {
        navigate(`${path}${search ? `?${search}` : ''}`, { replace: true });
      }
    },
    [
      location.search,
      isPersistSemenAcrossTabsEnabled,
      isSelectedSemenShippingEnabled,
      selectedSemenRef,
      navigate,
      setSearchParams,
    ],
  );

  const keepAliveTabs = useMemo(
    () => (
      <>
        {visitedTabs.has('home') && (
          <div className={activeTab === 'home' ? 'h-full' : 'hidden'}>
            <HomeTab />
          </div>
        )}
        {visitedTabs.has('shipping-integration') && (
          <div
            className={
              activeTab === 'shipping-integration' ? 'h-full' : 'hidden'
            }
          >
            <ShippingIntegrationTab
              isActive={activeTab === 'shipping-integration'}
            />
          </div>
        )}
        {visitedTabs.has('semen-pull') && (
          <div className={activeTab === 'semen-pull' ? 'h-full' : 'hidden'}>
            <SemenPullPage />
          </div>
        )}
      </>
    ),
    [visitedTabs, activeTab],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between">
        <WorkflowTabBar<SemenTab>
          tabs={SEMEN_TABS}
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
        {showSemenActions && (
          <SemenActions
            gridRef={homeTabGridRef}
            onFilterChanged={refreshHomeTabGrid}
          />
        )}
      </div>
      <div className="min-h-0 flex-1">
        {isPersistSemenAcrossTabsEnabled ? keepAliveTabs : <Outlet />}
      </div>
      {isPersistSemenAcrossTabsEnabled && (
        <ConfirmDialog
          visible={blocker.state === 'blocked'}
          onHide={() => blocker.reset?.()}
          header={t('common.warning')}
          message={`${t('common.unsavedChanges')} ${t('common.doYouWantToProceed')}`}
          icon="pi pi-exclamation-triangle"
          accept={() => {
            clearUnsavedChanges();
            blocker.proceed?.();
          }}
          reject={() => blocker.reset?.()}
          acceptLabel={t('common.yes')}
          rejectLabel={t('common.no')}
        />
      )}
    </div>
  );
};

const SemenPage = () => {
  const location = useLocation();
  const isDetail = location.pathname.includes('/detail');

  return (
    <SemenAppWrapper>
      {isDetail ? <Outlet /> : <SemenTabsView />}
    </SemenAppWrapper>
  );
};

export default SemenPage;
